"use client"

import React, { createContext, useContext, useEffect, useState, useCallback } from "react"
// Session restored from localStorage — no profile API call needed

interface User {
  [key: string]: unknown
}

interface AuthContextType {
  user: User | null
  token: string | null
  isLoading: boolean
  setAuth: (token: string, user: User) => void
  logout: () => void
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  isLoading: true,
  setAuth: () => {},
  logout: () => {},
  refreshUser: async () => {},
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const logout = useCallback(() => {
    localStorage.removeItem("auth_token")
    localStorage.removeItem("auth_user")
    setToken(null)
    setUser(null)
  }, [])

  const setAuth = useCallback((newToken: string, newUser: User) => {
    localStorage.setItem("auth_token", newToken)
    localStorage.setItem("auth_user", JSON.stringify(newUser))
    setToken(newToken)
    setUser(newUser)
  }, [])

  const refreshUser = useCallback(async () => {
    const storedUser = localStorage.getItem("auth_user")
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser)
        setUser(parsed)
      } catch {
        // ignore
      }
    }
  }, [])

  // Restore session on app load
  useEffect(() => {
    const storedToken = localStorage.getItem("auth_token")
    const storedUser = localStorage.getItem("auth_user")

    if (!storedToken || !storedUser) {
      if (storedToken) localStorage.removeItem("auth_token")
      if (storedUser) localStorage.removeItem("auth_user")
      setIsLoading(false)
      return
    }

    try {
      setToken(storedToken)
      setUser(JSON.parse(storedUser) as User)
    } catch {
      logout()
    }
    setIsLoading(false)
  }, [logout, refreshUser])

  return (
    <AuthContext.Provider value={{ user, token, isLoading, setAuth, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
